"use client";

import { useTranslations } from "next-intl";
import { useState, useEffect } from "react";
import { FaEnvelope, FaClock, FaInfoCircle, FaHeadset, FaUsers } from "react-icons/fa";
import Header from "@/components/header";
import Footer from "@/components/footer";

// Email obfuscation - split into parts to prevent bot scraping
const getEmail = () => {
	const parts = ['contact', '@', 'da4ndo', '.', 'com'];
	return parts.join('');
};

function ProtectedEmail({ label, buttonText }: { label: string; buttonText: string }) {
	const [email, setEmail] = useState<string>('');
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
		setEmail(getEmail());
	}, []);

	if (!mounted) {
		return (
			<div className="flex items-center gap-3 mb-4">
				<div className="rounded-lg bg-bme-main/10 p-3 text-bme-main">
					<FaEnvelope className="h-5 w-5" />
				</div>
				<div>
					<h3 className="text-sm font-medium text-gray-600 mb-1">{label}</h3>
					<div className="text-lg font-semibold text-gray-900 h-6 w-48 bg-gray-200 animate-pulse rounded" />
				</div>
			</div>
		);
	}

	return (
		<div className="flex items-center gap-3 mb-4">
			<div className="rounded-lg bg-bme-main/10 p-3 text-bme-main">
				<FaEnvelope className="h-5 w-5" />
			</div>
			<div>
				<h3 className="text-sm font-medium text-gray-600 mb-1">{label}</h3>
				<a
					href={`mailto:${email}`}
					className="text-lg font-semibold text-gray-900 hover:text-bme-main transition-colors"
				>
					{email}
				</a>
			</div>
		</div>
	);
}

function ProtectedEmailButton({ buttonText }: { buttonText: string }) {
	const [email, setEmail] = useState<string>('');
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
		setEmail(getEmail());
	}, []);

	if (!mounted) {
		return (
			<div className="h-10 w-32 bg-gray-200 animate-pulse rounded-lg" />
		);
	}

	return (
		<a
			href={`mailto:${email}`}
			className="cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-bme-main hover:bg-bme-main-hover text-white transition shadow-md hover:shadow-lg"
		>
			<FaEnvelope className="h-4 w-4" />
			{buttonText}
		</a>
	);
}

export default function Contact() {
	const t = useTranslations("contact");
	const headerT = useTranslations("header");
	const coreT = useTranslations("core");

	return (
		<div className="flex flex-col min-h-screen bg-gray-50 smooth-scroll">
			<Header name={coreT("title")} desc={headerT("description")} />

			{/* Hero Section */}
			<section className="relative bg-linear-to-br from-gray-900 to-black text-white">
				<div className="container mx-auto max-w-7xl px-4 py-12 sm:py-16 md:py-20">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 items-center">
						<div>
							<h1 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
								{t("title")}
							</h1>
							<p className="mt-4 text-white/80 text-base sm:text-lg leading-relaxed">
								{t("heroDescription")}
							</p>
						</div>
						<div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-6">
							<div className="grid grid-cols-2 gap-4 text-center">
								<div className="p-4">
									<div className="text-2xl sm:text-3xl font-bold text-bme-main mb-2">
										<FaHeadset className="inline-block mx-auto" />
									</div>
									<div className="text-xs text-white/70">
										{t("hero.support")}
									</div>
									<div className="text-xs text-white/50 mt-1">
										{t("hero.supportDesc")}
									</div>
								</div>
								<div className="p-4">
									<div className="text-2xl sm:text-3xl font-bold text-bme-main mb-2">
										<FaUsers className="inline-block mx-auto" />
									</div>
									<div className="text-xs text-white/70">
										{t("hero.team")}
									</div>
									<div className="text-xs text-white/50 mt-1">
										{t("hero.teamDesc")}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="absolute bottom-0 left-0 right-0 h-1 bg-linear-to-r from-transparent via-bme-main to-transparent opacity-80" />
			</section>

			<main className="grow">
				{/* Contact Information Section */}
				<section className="container mx-auto max-w-7xl px-4 py-10">
					<div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
						<div className="flex items-center gap-3 mb-6">
							<FaInfoCircle className="text-bme-main text-xl" />
							<h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
								{t("info.title")}
							</h2>
						</div>
						<p className="text-gray-600 mb-8 text-sm sm:text-base">
							{t("info.description")}
						</p>

						{/* Email Card */}
						<div className="rounded-xl border border-gray-200 bg-gray-50 p-6 mb-6">
							<ProtectedEmail label={t("email.label")} buttonText={t("email.button")} />
							<ProtectedEmailButton buttonText={t("email.button")} />
						</div>

						{/* Response Time Card */}
						<div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
							<div className="flex items-center gap-3 mb-4">
								<div className="rounded-lg bg-bme-main/10 p-3 text-bme-main">
									<FaClock className="h-5 w-5" />
								</div>
								<div>
									<h3 className="text-sm font-medium text-gray-600 mb-1">
										{t("response.title")}
									</h3>
									<p className="text-base text-gray-900">
										{t("response.description")}
									</p>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* Additional Info Section */}
				<section className="container mx-auto max-w-7xl px-4 py-10">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div className="rounded-xl border border-gray-200 bg-white p-6">
							<h3 className="font-medium text-gray-800 mb-2">
								{t("info.title")}
							</h3>
							<p className="text-sm text-gray-600">
								{t("description")}
							</p>
						</div>
						<div className="rounded-xl border border-gray-200 bg-white p-6">
							<h3 className="font-medium text-gray-800 mb-2">
								{t("response.title")}
							</h3>
							<p className="text-sm text-gray-600">
								{t("response.description")}
							</p>
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
